import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import KPITrendChart from "../KPITrendChart/KPITrendChart";
import { DashboardKPI } from "../../types";
import "./KPICard.css";

interface KPICardProps {
  kpi: DashboardKPI;
}

/** Kicker on the back face: CertainPath red on white, amber on slate. */
const BACK_EYEBROW =
  "mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-primary dark:text-amber-400";

/** Matches the page's kicker treatment. See MemberDashboardPage. */
const EYEBROW =
  "text-xs font-semibold uppercase tracking-[0.12em] text-amber-600 dark:text-amber-400 mb-1.5";

/**
 * Names the accounts a card is missing, as the QuickBooks mapping screen names
 * them — that is where the member goes to look for them.
 */
const formatAccountList = (labels: string[]): string => {
  if (labels.length <= 1) {
    return labels[0] ?? "an account this card needs";
  }

  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};

const KPICard: React.FC<KPICardProps> = ({ kpi }) => {
  const [flipped, setFlipped] = useState(false);

  const toggle = () => setFlipped((current) => !current);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <div
      aria-label={`${kpi.name}. ${flipped ? "Showing how it is calculated" : "Showing the trend"}. Activate to flip.`}
      aria-pressed={flipped}
      className={`kpi-card cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:focus-visible:ring-slate-500 ${
        flipped ? "is-flipped" : ""
      }`}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="relative">
        <div className="kpi-card__face kpi-card__front">
          <div className="flex min-h-[32rem] flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-start justify-between gap-3">
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                {kpi.source}
              </span>
            </div>

            <div>
              <h3 className="text-xl font-bold tracking-tight text-secondary dark:text-white">
                {kpi.name}
              </h3>
              {/*
                An unavailable card shows no headline at all rather than a dash.
                A dash reads as "we looked and found nothing"; this card has not
                looked, because the member keeps no account to look in.
              */}
              {kpi.isAvailable && (
                <>
                  <div className="mt-2 flex flex-wrap items-baseline gap-3">
                    <span className="text-5xl font-bold leading-none tracking-tight text-secondary dark:text-white">
                      {kpi.valueLabel ?? "—"}
                    </span>
                    {kpi.deltaLabel && (
                      <span
                        className={`text-sm ${
                          kpi.deltaIsGood
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-red-700 dark:text-red-400"
                        }`}
                      >
                        {kpi.deltaIsUp ? "↑" : "↓"} {kpi.deltaLabel}
                      </span>
                    )}
                  </div>
                  {kpi.monthLabel && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {kpi.monthLabel}
                    </p>
                  )}
                </>
              )}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300">
              {kpi.definition}
            </p>

            {kpi.isAvailable ? (
              <>
                <KPITrendChart series={kpi.chart} />

                {kpi.isIncomplete && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
                    Some QuickBooks accounts aren&apos;t mapped and confirmed
                    yet, so this figure is incomplete.
                  </p>
                )}

                <div className="border-t border-gray-200 pt-4 dark:border-slate-700">
                  <div className={EYEBROW}>What to look for</div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {kpi.guidance}
                  </p>
                </div>
              </>
            ) : (
              /*
                Not an error, and not a warning — the member's mapping is
                finished and correct. Neutral treatment, and the guidance is
                dropped: it describes a trend that does not exist.

                The copy describes the *mapping*, never the business. Nothing
                here can tell "you don't track field labor" apart from "you
                mapped your field labor account to Other Direct Cost", and the
                second is the likelier of the two — so it says what is missing
                and how to fix it rather than telling a member what their own
                books contain.
              */
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/50">
                <div className={EYEBROW}>Not available</div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {/*
                    "Field Labor" stays bare, with no "account" after it: it is
                    the exact option the member picks under the mapping
                    screen's "CertainPath account" column, and repeating the
                    word would put "account" in one sentence twice with two
                    different referents — theirs and ours.
                  */}
                  No QuickBooks account is mapped to{" "}
                  {formatAccountList(kpi.missingAccountLabels)} yet. Please map
                  one to see this card.
                </p>
              </div>
            )}

            <div className="mt-auto flex items-center gap-2 pt-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
              <RefreshCw className="h-4 w-4" />
              Tap card for the formula
            </div>
          </div>
        </div>

        <div className="kpi-card__face kpi-card__back">
          {/*
            Two palettes, one structure. Light mode is a white card with
            CertainPath red as the accent; dark mode borrows the nav shell's
            slate-900 with amber, so the back reads as part of the app.

            The accent has to change with the surface — amber on white is
            roughly 1.7:1 against text, and red on slate-900 is little better.
            Each palette picks the brand colour that survives its background.
          */}
          <div className="flex h-full flex-col gap-5 overflow-auto rounded-lg border border-gray-200 bg-white p-6 dark:border-transparent dark:bg-slate-900">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className={BACK_EYEBROW}>How we calculate it</div>
                <h3 className="text-xl font-bold tracking-tight text-secondary dark:text-white">
                  {kpi.name}
                </h3>
              </div>
              <span className="flex-none rounded-full border border-secondary/25 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.07em] text-secondary/70 dark:border-white/20 dark:text-white/60">
                {kpi.source}
              </span>
            </div>

            {/*
              CertainPath red in both themes, so the formula reads the same way
              on a white card as on a slate one. Everything on it is white —
              nothing else clears contrast against this red at body size.
            */}
            <div className="flex items-center justify-center gap-4 rounded-xl bg-primary px-5 py-6">
              {/*
                A KPI with no denominator is a single figure, not a ratio — a
                balance sheet card has nothing to divide by. The rule and the
                bottom line are dropped rather than left empty, so the block
                reads as a statement instead of a fraction missing its half.
              */}
              <div className="flex max-w-xs flex-col items-center text-center">
                <span
                  className={`text-sm font-semibold leading-snug text-white ${kpi.formula.denominator ? "pb-2.5" : ""}`}
                >
                  {kpi.formula.numerator}
                </span>
                {kpi.formula.denominator && (
                  <>
                    <span className="w-full border-t-2 border-white" />
                    <span className="pt-2.5 text-sm font-semibold leading-snug text-white">
                      {kpi.formula.denominator}
                    </span>
                  </>
                )}
              </div>
              {kpi.formula.multiplier && (
                <span className="whitespace-nowrap text-xl font-bold text-white">
                  {kpi.formula.multiplier}
                </span>
              )}
            </div>

            {kpi.formula.workedExample && (
              <div className="rounded-r-lg border-l-[3px] border-primary bg-primary/5 px-4 py-3 dark:border-amber-400 dark:bg-amber-400/10">
                <div className={BACK_EYEBROW}>
                  Your numbers for {kpi.monthLabel}
                </div>
                <p className="text-lg font-semibold text-secondary dark:text-white">
                  {kpi.formula.workedExample}
                </p>
              </div>
            )}

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-white/45">
                What goes into it
              </div>
              <p className="text-sm leading-relaxed text-gray-600 dark:text-white/80">
                {kpi.formula.note}
              </p>
            </div>

            <div className="mt-auto flex items-center gap-2 border-t border-gray-200 pt-4 text-xs font-semibold text-primary dark:border-white/10 dark:text-amber-400">
              <RefreshCw className="h-4 w-4" />
              Tap card to flip back
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPICard;
